import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { ExportLcService } from '../../services/exportLc.service';
import { CreateExportLcRequest } from '../../interfaces/commercial.interface';
import CommercialPartyAutocomplete from './CommercialPartyAutocomplete';
import BuyerAutocomplete from '../merchandising/BuyerAutocomplete';
import BuyerOrderRefPicker from './common/BuyerOrderRefPicker';
import { BuyerService } from '../../services/buyer.service';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { toLocalIsoDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();

const EMPTY = {
    lcNumber: '', issuingBank: '', advisingBank: '',
    buyerId: '', buyerName: '', salesOrderId: '', salesOrderRef: '',
    currencyCode: '', lcAmount: '', tolerancePercent: '0', b2bEntitlementPercent: '',
    issueDate: today(), expiryDate: today(), latestShipmentDate: '', notes: '',
};

/**
 * The buyer and the buyer order are chosen from pickers. The record keeps the picked buyer's name
 * and the order's number alongside their ids, so lists and documents can show them as text.
 */
const ExportLcForm: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;

    const [form, setForm] = useState({ ...EMPTY });
    const [issuingBankPartyId, setIssuingBankPartyId] = useState<string | null>(null);
    const [advisingBankPartyId, setAdvisingBankPartyId] = useState<string | null>(null);
    const [buyer, setBuyer] = useState<IBuyer | null>(null);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const institutionId = AuthService.getSelectedInstitution() ?? '';

    useEffect(() => {
        if (!id) return;
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        (async () => {
            try {
                setLoading(true);
                const lc = await ExportLcService.getById(institutionId, id);
                setForm({
                    lcNumber: lc.lcNumber, issuingBank: lc.issuingBank, advisingBank: lc.advisingBank ?? '',
                    buyerId: lc.buyerId ?? '', buyerName: lc.buyerName,
                    salesOrderId: lc.salesOrderId ?? '', salesOrderRef: lc.salesOrderRef ?? '',
                    currencyCode: lc.currencyCode, lcAmount: String(lc.lcAmount),
                    tolerancePercent: String(lc.tolerancePercent),
                    b2bEntitlementPercent: lc.b2bEntitlementPercent != null ? String(lc.b2bEntitlementPercent) : '',
                    issueDate: lc.issueDate, expiryDate: lc.expiryDate,
                    latestShipmentDate: lc.latestShipmentDate ?? '', notes: lc.notes ?? '',
                });
                // Fix round 1: the picker pre-selects from the bare id -- CommercialPartyAutocomplete
                // resolves it against its loaded options or fetches it individually if needed.
                if (lc.buyerId) {
                    try {
                        setBuyer(await BuyerService.getById(institutionId, lc.buyerId));
                    } catch (buyerErr) {
                        // The saved buyer name still shows; the picker just starts empty.
                        toast.showApiError(buyerErr, 'Could not load the linked buyer');
                    }
                }
                setIssuingBankPartyId(lc.issuingBankPartyId ?? null);
                setAdvisingBankPartyId(lc.advisingBankPartyId ?? null);
            } catch (e) {
                toast.showApiError(e, 'Failed to load LC');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const validate = (): string | null => {
        if (!form.buyerName.trim()) return 'Please choose the buyer.';
        if (!form.lcNumber.trim()) return 'LC number is required.';
        if (!form.issuingBank.trim()) return 'Issuing bank is required.';
        if (!form.currencyCode.trim() || form.currencyCode.trim().length !== 3) return 'Currency code must be a 3-letter ISO code.';
        if (!form.lcAmount || Number(form.lcAmount) <= 0) return 'LC amount must be greater than zero.';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);

        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;

        const dto: CreateExportLcRequest = {
            lcNumber: form.lcNumber.trim(),
            issuingBank: form.issuingBank.trim(),
            advisingBank: form.advisingBank.trim() || undefined,
            issuingBankPartyId: issuingBankPartyId ?? undefined,
            advisingBankPartyId: advisingBankPartyId ?? undefined,
            buyerId: form.buyerId.trim() || undefined,
            // The linked buyer's current name wins over the saved snapshot, so a renamed buyer is picked up on save.
            buyerName: (buyer && buyer.id === form.buyerId ? buyer.name : form.buyerName).trim(),
            salesOrderId: form.salesOrderId.trim() || undefined,
            salesOrderRef: form.salesOrderRef.trim() || undefined,
            currencyCode: form.currencyCode.trim().toUpperCase(),
            lcAmount: Number(form.lcAmount),
            tolerancePercent: Number(form.tolerancePercent || 0),
            b2bEntitlementPercent: form.b2bEntitlementPercent.trim() ? Number(form.b2bEntitlementPercent) : undefined,
            issueDate: form.issueDate,
            expiryDate: form.expiryDate,
            latestShipmentDate: form.latestShipmentDate || undefined,
            notes: form.notes.trim() || undefined,
        };

        try {
            setSaving(true);
            const saved = isEdit
                ? await ExportLcService.update(institutionId, id!, dto)
                : await ExportLcService.create(institutionId, dto);
            toast.showSuccess(isEdit ? 'LC updated' : 'LC created');
            navigate(`/commercial/lc/${saved.id}`);
        } catch (err) {
            toast.showApiError(err, isEdit ? 'Failed to update LC' : 'Failed to create LC');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3, maxWidth: 900 }} data-help-page="commercial.lcForm.page">
            <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Edit Export LC' : 'New Export LC'}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Paper sx={{ p: 3 }} data-help-section="commercial.lcForm.section.details">
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.lcForm.field.lcNumber" label="LC Number" fullWidth required value={form.lcNumber} onChange={set('lcNumber')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.lcForm.field.issuingBank" label="Issuing Bank" fullWidth required value={form.issuingBank} onChange={set('issuingBank')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.lcForm.field.advisingBank" label="Advising Bank" fullWidth value={form.advisingBank} onChange={set('advisingBank')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            {/* Picking a bank sets the id AND fills the text field above; the text
                                stays editable afterwards. Clearing the picker clears only the id --
                                the typed bank name is left untouched. */}
                            <CommercialPartyAutocomplete
                                institutionId={institutionId}
                                partyTypes={['BANK']}
                                data-help="commercial.lcForm.field.issuingBankParty"
                                label="Issuing Bank (party lookup)"
                                value={issuingBankPartyId}
                                onChange={(party) => {
                                    setIssuingBankPartyId(party?.id ?? null);
                                    if (party) setForm(f => ({ ...f, issuingBank: party.name }));
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <CommercialPartyAutocomplete
                                institutionId={institutionId}
                                partyTypes={['BANK']}
                                data-help="commercial.lcForm.field.advisingBankParty"
                                label="Advising Bank (party lookup)"
                                value={advisingBankPartyId}
                                onChange={(party) => {
                                    setAdvisingBankPartyId(party?.id ?? null);
                                    if (party) setForm(f => ({ ...f, advisingBank: party.name }));
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            {/* The currency cannot change once the LC exists, so it is locked in edit mode. */}
                            <TextField
                                select data-help="commercial.lcForm.field.currency" label="Currency Code" fullWidth required
                                value={form.currencyCode}
                                // Not just visually disabled: the handler itself is dropped in edit
                                // mode so no synthetic/forced change event can mutate state either.
                                onChange={isEdit ? undefined : set('currencyCode')}
                                disabled={isEdit}
                                helperText={isEdit ? 'Currency cannot be changed after creation.' : undefined}
                            >
                                {(form.currencyCode && !['USD', 'EUR', 'GBP', 'BDT'].includes(form.currencyCode)
                                    ? [...['USD', 'EUR', 'GBP', 'BDT'], form.currencyCode]
                                    : ['USD', 'EUR', 'GBP', 'BDT']
                                ).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <BuyerAutocomplete
                                data-help="commercial.lcForm.field.buyer"
                                value={buyer}
                                required
                                size="medium"
                                helperText={!buyer && form.buyerName ? `Saved buyer: ${form.buyerName}. Pick the buyer to link it.` : undefined}
                                onChange={(picked) => {
                                    setBuyer(picked);
                                    // A different buyer invalidates the order picked for the previous one.
                                    setForm(f => ({
                                        ...f,
                                        buyerId: picked?.id ?? '',
                                        buyerName: picked?.name ?? f.buyerName,
                                        ...(picked && picked.id !== f.buyerId ? { salesOrderId: '', salesOrderRef: '' } : {}),
                                    }));
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <BuyerOrderRefPicker
                                data-help="commercial.lcForm.field.buyerOrder"
                                institutionId={institutionId}
                                buyerId={form.buyerId || null}
                                value={form.salesOrderId || null}
                                valueLabel={form.salesOrderRef || null}
                                helperText={form.buyerId ? undefined : 'Pick a buyer to see only their orders.'}
                                onChange={(orderId, order) => setForm(f => ({
                                    ...f, salesOrderId: orderId ?? '', salesOrderRef: order?.orderNumber ?? '',
                                }))}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.lcForm.field.lcAmount" label="LC Amount" type="number" fullWidth required value={form.lcAmount} onChange={set('lcAmount')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.lcForm.field.tolerance" label="Tolerance %" type="number" fullWidth value={form.tolerancePercent} onChange={set('tolerancePercent')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.lcForm.field.b2bPercent" label="B2B Entitlement % (blank = institution default)" type="number" fullWidth
                                value={form.b2bEntitlementPercent} onChange={set('b2bEntitlementPercent')} />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.lcForm.field.issueDate" label="Issue Date" type="date" fullWidth required InputLabelProps={{ shrink: true }}
                                value={form.issueDate} onChange={set('issueDate')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.lcForm.field.expiryDate" label="Expiry Date" type="date" fullWidth required InputLabelProps={{ shrink: true }}
                                value={form.expiryDate} onChange={set('expiryDate')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField data-help="commercial.lcForm.field.latestShipmentDate" label="Latest Shipment Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                                value={form.latestShipmentDate} onChange={set('latestShipmentDate')} />
                        </Grid>

                        <Grid size={12}>
                            <TextField data-help="commercial.lcForm.field.notes" label="Notes" fullWidth multiline rows={3} value={form.notes} onChange={set('notes')} />
                        </Grid>

                        <Grid size={12} sx={{ display: 'flex', gap: 1 }}>
                            <Button data-help="commercial.lcForm.save" type="submit" variant="contained" disabled={saving}>
                                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create LC'}
                            </Button>
                            <Button data-help="commercial.lcForm.cancel" variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default ExportLcForm;
